import type { RouterContractOutputs } from "@biteasy/contracts";
import { useAuth } from "@/auth";
import { useNewAppDialog } from "@/hooks/use-new-app-dialog";
import { useSendVerificationEmail } from "@/hooks/use-send-verification-email";

type ChecklistStatus = RouterContractOutputs["onboarding"]["checklist"];

interface NavigateAction {
	to: string;
	search: Record<string, unknown>;
}

export interface ChecklistItem {
	key: string;
	label: string;
	completed: boolean;
	action: (() => void) | NavigateAction;
}

/** Builds the ordered checklist items from API status data. */
export function useOnboardingChecklistItems(
	status: ChecklistStatus,
): ChecklistItem[] {
	const { user } = useAuth();
	const { open: openNewAppDialog } = useNewAppDialog();
	const sendVerificationEmail = useSendVerificationEmail();

	const appHref = status.firstAppId
		? `/apps/${status.firstAppId}`
		: "/dashboard";

	return [
		{
			key: "create_app",
			label: "Create your first app",
			completed: status.hasApp,
			action: openNewAppDialog,
		},
		{
			key: "verify_email",
			label: "Verify your email address",
			completed: user.emailVerified,
			action: () => sendVerificationEmail(),
		},
		{
			key: "add_partner",
			label: "Add your first partner",
			completed: status.hasPartner,
			action: { to: "/partners", search: { create: true } },
		},
		{
			key: "create_referral_link",
			label: "Create a referral link",
			completed: status.hasReferralLink,
			action: { to: appHref, search: { create: true } },
		},
		{
			key: "add_payment_method",
			label: "Add a payment method",
			completed: status.hasPaymentMethod,
			action: {
				to: "/finances",
				search: { tab: "payment_methods", add: true },
			},
		},
		{
			key: "connect_billing",
			label: "Connect billing to your app",
			completed: status.hasAppWithBilling,
			action: {
				to: appHref,
				search: { tab: "settings", highlight: "paymentMethod" },
			},
		},
		{
			key: "setup_revenue_tracking",
			label: "Set up revenue tracking",
			completed: status.hasRevenueTracking,
			action: {
				to: appHref,
				search: { tab: "settings", highlight: "iap" },
			},
		},
	];
}
